import { useAuth0 } from '@auth0/auth0-react'

import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useHistory } from 'react-router-dom'

import { clearSignupFlow, isSignupFlowActive, ONBOARDING_SEEN_KEY } from '../config/auth0'
import { Agency } from '../models/Agency.model'
import { Hospital } from '../models/Hospital.model'
import { NursingSpecializations } from '../models/NursingSpecializations.model'
import { PositionTitles } from '../models/PositionTitles.model'
import { AgencyService } from '../services/AgencyService/AgencyService'
import { HospitalService } from '../services/HospitalService/HospitalService'
import UserService from '../services/UserService/UserService'
import { shouldShowSignupVerifyPrompt, SIGNUP_VERIFY_FLOW_UPDATED_EVENT } from '../utils/emailVerification'

const hasSeenOnboarding = (sub: string): boolean => {
  try {
    const list: string[] = JSON.parse(localStorage.getItem(ONBOARDING_SEEN_KEY) || '[]')
    return list.includes(sub)
  } catch {
    return false
  }
}

const markOnboardingDone = (sub: string): void => {
  try {
    const list: string[] = JSON.parse(localStorage.getItem(ONBOARDING_SEEN_KEY) || '[]')
    if (!list.includes(sub)) {
      list.push(sub)
      localStorage.setItem(ONBOARDING_SEEN_KEY, JSON.stringify(list))
    }
  } catch {
    // ignore
  }
}

const steps = [
  { id: 1, label: 'Welcome' },
  { id: 2, label: 'Profile' },
  { id: 3, label: 'Find hospital' },
]

const Step1 = () => (
  <>
    {/* Teal header */}
    <div
      className="rounded-t-2xl px-8 pt-10 pb-8 text-center"
      style={{ background: 'linear-gradient(135deg, #308b97 0%, #3aa8b5 100%)' }}
    >
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
          <img src="/assets/icon/icon.png" alt="Scrubstr" className="w-10 h-10 object-contain" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Welcome to Scrubstr</h2>
      <p className="text-white text-opacity-90 text-sm">Real reviews from the breakroom, not the boardroom</p>
    </div>

    {/* Feature list */}
    <div className="px-8 py-6 space-y-5">
      {/* Search */}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-aqua-mint bg-opacity-15 flex items-center justify-center">
          <svg className="w-5 h-5 text-aqua-mint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <div>
          <p className="font-bold text-graphite-grey text-base">Search hospitals &amp; agencies</p>
          <p className="text-gray-500 text-sm leading-snug mt-0.5">
            Browse 7,400+ facilities rated by real healthcare workers across the country
          </p>
        </div>
      </div>

      {/* Share */}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-aqua-mint bg-opacity-15 flex items-center justify-center">
          <svg className="w-5 h-5 text-aqua-mint" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>
        <div>
          <p className="font-bold text-graphite-grey text-base">Share your experience</p>
          <p className="text-gray-500 text-sm leading-snug mt-0.5">
            Your anonymous review helps fellow healthcare workers make better career decisions
          </p>
        </div>
      </div>

      {/* Anonymous */}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-aqua-mint bg-opacity-15 flex items-center justify-center">
          <svg className="w-5 h-5 text-aqua-mint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
        <div>
          <p className="font-bold text-graphite-grey text-base">Always anonymous</p>
          <p className="text-gray-500 text-sm leading-snug mt-0.5">
            Your name is never attached to your reviews — ever
          </p>
        </div>
      </div>
    </div>
  </>
)

const WORK_EXPERIENCE_OPTIONS = ['Less than 1 year', '1–2 years', '3–5 years', '6–10 years', '10+ years']

const WORK_SETTINGS_OPTIONS = ['Travel', 'Staff', 'Per Diem']

const nonRestrictedRoles = ['RN', 'LPN/LVN', 'RT']

interface OnboardingFormData {
  position_title: string
  nursing_specialization: string
  work_experience: string
  work_settings: string
}

interface Step2Props {
  formData: OnboardingFormData
  onChange: (field: keyof OnboardingFormData, value: string) => void
  saving: boolean
  error: string
}

const Step2 = ({ formData, onChange, saving, error }: Step2Props) => {
  const showSpecialty = nonRestrictedRoles.includes(formData.position_title)

  return (
    <>
      <div
        className="rounded-t-2xl px-8 pt-8 pb-6"
        style={{ background: 'linear-gradient(135deg, #308b97 0%, #3aa8b5 100%)' }}
      >
        <h2 className="text-xl font-bold text-white mb-1">Tell us about yourself</h2>
        <p className="text-white text-sm" style={{ opacity: 0.85 }}>
          Adds credibility to your reviews — never shown publicly
        </p>
      </div>

      <div className="px-8 py-5 space-y-4">
        {/* Discipline */}
        <div>
          <label className="block text-sm font-semibold text-graphite-grey mb-1.5">Discipline</label>
          <select
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-aqua-mint bg-white text-sm"
            value={formData.position_title}
            onChange={(e) => onChange('position_title', e.target.value)}
          >
            <option value="">— Select —</option>
            {Object.keys(PositionTitles)
              .sort((a, b) => a.localeCompare(b))
              .map((label) => {
                const value = PositionTitles[label as keyof typeof PositionTitles]
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                )
              })}
          </select>
        </div>

        {/* Specialty — only for RN/LPN/RT */}
        {showSpecialty && (
          <div>
            <label className="block text-sm font-semibold text-graphite-grey mb-1.5">Specialty</label>
            <select
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-aqua-mint bg-white text-sm"
              value={formData.nursing_specialization}
              onChange={(e) => onChange('nursing_specialization', e.target.value)}
            >
              <option value="">— Select —</option>
              {Object.keys(NursingSpecializations)
                .sort((a, b) => a.localeCompare(b))
                .map((label) => {
                  const value = NursingSpecializations[label as keyof typeof NursingSpecializations]
                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                })}
            </select>
          </div>
        )}

        {/* Years of experience */}
        <div>
          <label className="block text-sm font-semibold text-graphite-grey mb-1.5">Years of experience</label>
          <select
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-aqua-mint bg-white text-sm"
            value={formData.work_experience}
            onChange={(e) => onChange('work_experience', e.target.value)}
          >
            <option value="">— Select —</option>
            {WORK_EXPERIENCE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {/* Work setting */}
        <div>
          <label className="block text-sm font-semibold text-graphite-grey mb-1.5">Work setting</label>
          <select
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-aqua-mint bg-white text-sm"
            value={formData.work_settings}
            onChange={(e) => onChange('work_settings', e.target.value)}
          >
            <option value="">— Select —</option>
            {WORK_SETTINGS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}
        {saving && <p className="text-gray-400 text-sm text-center">Saving…</p>}
      </div>
    </>
  )
}

type AutocompleteResult = {
  id: number
  name: string
  type: 'hospital' | 'agency'
  permalink: string | null
  rating?: number
  city?: string | null
  state?: { name?: string; code?: string } | null
}

interface Step3Props {
  searchQuery: string
  onSearchChange: (q: string) => void
  autocompleteResults: AutocompleteResult[]
  isSearching: boolean
  onSelectResult: (result: AutocompleteResult) => void
}

const Step3 = ({ searchQuery, onSearchChange, autocompleteResults, isSearching, onSelectResult }: Step3Props) => {
  const [focused, setFocused] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const showDropdown = focused && searchQuery.trim().length >= 1 && (autocompleteResults.length > 0 || isSearching)

  return (
    <>
      {/* Teal header */}
      <div
        className="rounded-t-2xl px-8 pt-8 pb-6"
        style={{ background: 'linear-gradient(135deg, #308b97 0%, #3aa8b5 100%)' }}
      >
        <h2 className="text-xl font-bold text-white mb-1">Write your first review</h2>
        <p className="text-white text-sm" style={{ opacity: 0.85 }}>
          Help a fellow healthcare worker make a better decision
        </p>
      </div>

      <div className="px-8 py-6">
        {/* Hospital card */}
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-6 flex flex-col items-center text-center mb-5">
          {/* Hospital icon */}
          <div className="mb-4">
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="8" y="18" width="40" height="32" rx="2" fill="#e0e7ef" />
              <rect x="4" y="18" width="48" height="6" rx="2" fill="#b0bec5" />
              <rect x="14" y="30" width="8" height="8" rx="1" fill="white" />
              <rect x="34" y="30" width="8" height="8" rx="1" fill="white" />
              <rect x="22" y="34" width="12" height="16" rx="1" fill="white" />
              {/* Red cross */}
              <rect x="24" y="4" width="8" height="20" rx="2" fill="#e53e3e" />
              <rect x="18" y="10" width="20" height="8" rx="2" fill="#e53e3e" />
            </svg>
          </div>
          <p className="font-bold text-graphite-grey text-base mb-1">Have you worked at a hospital or agency?</p>
          <p className="text-gray-500 text-sm leading-snug">
            Search for it and leave an anonymous review. Takes about 2 minutes.
          </p>
        </div>

        {/* Search input with autocomplete */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search for a hospital or agency..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={(e) => {
              // Don't close if clicking inside the dropdown
              if (dropdownRef.current?.contains(e.relatedTarget as Node)) return
              setFocused(false)
            }}
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-aqua-mint bg-white text-sm"
          />

          {/* Autocomplete dropdown — floats outside the card */}
          {showDropdown && (
            <div
              ref={dropdownRef}
              className="absolute left-0 right-0 top-full z-30 mt-1 max-h-52 overflow-y-auto rounded-lg border border-gray-100 bg-white shadow-xl"
            >
              {isSearching && autocompleteResults.length === 0 ? (
                <div className="p-3 text-center text-sm text-gray-400">Searching…</div>
              ) : (
                autocompleteResults.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    type="button"
                    className="flex w-full items-start gap-2.5 border-b border-gray-50 p-3 text-left hover:bg-gray-50 transition-colors last:border-0"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setFocused(false)
                      onSelectResult(result)
                    }}
                  >
                    {/* Icon */}
                    <div
                      className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white text-xs font-bold ${
                        result.type === 'hospital' ? 'bg-aqua-mint' : 'bg-orange'
                      }`}
                    >
                      {result.type === 'hospital' ? 'H' : 'A'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-sm font-medium text-graphite-grey truncate"
                        dangerouslySetInnerHTML={{ __html: result.name }}
                      />
                      <p className="text-xs text-gray-400 mt-0.5">
                        {result.type === 'hospital' ? 'Hospital' : 'Agency'}
                        {result.city || result.state?.name
                          ? ` · ${[result.city, result.state?.name].filter(Boolean).join(', ')}`
                          : ''}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export const OnboardingModal: React.FC = () => {
  const { user, isAuthenticated } = useAuth0()
  const history = useHistory()
  const [currentStep, setCurrentStep] = useState(0)
  const [visible, setVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [autocompleteResults, setAutocompleteResults] = useState<AutocompleteResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedResult, setSelectedResult] = useState<AutocompleteResult | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hospitalServiceRef = useRef(new HospitalService())
  const agencyServiceRef = useRef(new AgencyService())
  const [formData, setFormData] = useState<OnboardingFormData>({
    nursing_specialization: '',
    position_title: '',
    work_experience: '',
    work_settings: '',
  })

  const tryOpenOnboarding = () => {
    if (!isAuthenticated || !user?.sub) return
    if (shouldShowSignupVerifyPrompt(isAuthenticated, user)) return

    const isSignupFlow = isSignupFlowActive()
    if (!isSignupFlow) return

    if (hasSeenOnboarding(user.sub)) {
      clearSignupFlow()
      return
    }

    setVisible(true)
  }

  useEffect(() => {
    tryOpenOnboarding()

    const onSignupVerifyFlowUpdated = () => tryOpenOnboarding()
    window.addEventListener(SIGNUP_VERIFY_FLOW_UPDATED_EVENT, onSignupVerifyFlowUpdated)
    return () => window.removeEventListener(SIGNUP_VERIFY_FLOW_UPDATED_EVENT, onSignupVerifyFlowUpdated)
  }, [isAuthenticated, user?.sub])

  // Fallback: re-check when user.sub resolves (in case isAuthenticated fired first)
  useEffect(() => {
    if (!isAuthenticated || !user?.sub || visible) return
    if (shouldShowSignupVerifyPrompt(isAuthenticated, user)) return

    const isSignupFlow = isSignupFlowActive()
    if (isSignupFlow && !hasSeenOnboarding(user.sub)) {
      setVisible(true)
    }
  }, [user?.sub, isAuthenticated, visible, user])

  // Debounced autocomplete search — fires 300ms after user stops typing
  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setSelectedResult(null) // Clear selection when user types

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    const trimmed = query.trim()
    if (trimmed.length < 1) {
      setAutocompleteResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    debounceTimerRef.current = setTimeout(async () => {
      try {
        // Use Promise.allSettled so a failure in one doesn't lose the other's results
        const [hospitalSettled, agencySettled] = await Promise.allSettled([
          hospitalServiceRef.current.search({ autocomplete: true, page: 1, sort: 'az', term: trimmed }),
          agencyServiceRef.current.search({ autocomplete: true, page: 1, sort: 'az', term: trimmed }),
        ])

        const hospitals = hospitalSettled.status === 'fulfilled' ? hospitalSettled.value.hospitals : []
        const agencies = agencySettled.status === 'fulfilled' ? agencySettled.value.agencies : []

        const combined: AutocompleteResult[] = [
          ...hospitals.slice(0, 5).map((h: Hospital) => ({
            city: h.city,
            id: h.id,
            name: h.name,
            permalink: h.permalink,
            rating: h.rating,
            state: h.state,
            type: 'hospital' as const,
          })),
          ...agencies.slice(0, 5).map((a: Agency) => ({
            city: null,
            id: a.id,
            name: a.name,
            permalink: a.permalink,
            rating: a.rating,
            state: null,
            type: 'agency' as const,
          })),
        ]
        setAutocompleteResults(combined)
      } catch {
        // Silently ignore unexpected errors
      } finally {
        setIsSearching(false)
      }
    }, 300)
  }

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  const handleSelectResult = (result: AutocompleteResult) => {
    setSelectedResult(result)
    setSearchQuery(result.name.replace(/<[^>]*>/g, '')) // Strip HTML tags from name
  }

  const handleFormChange = (field: keyof OnboardingFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      // Clear specialty when discipline changes to a non-restricted role
      ...(field === 'position_title' && !nonRestrictedRoles.includes(value) ? { nursing_specialization: '' } : {}),
      [field]: value,
    }))
  }

  const saveProfileData = async (): Promise<boolean> => {
    if (!user?.sub) return true
    // Only save if at least one field is filled
    const hasData = formData.position_title || formData.work_experience || formData.work_settings
    if (!hasData) return true
    setSaving(true)
    setSaveError('')
    try {
      const payload: Partial<
        Record<'position_title' | 'nursing_specialization' | 'work_experience' | 'work_settings', string>
      > = {}
      if (formData.position_title) payload.position_title = formData.position_title
      if (formData.nursing_specialization) payload.nursing_specialization = formData.nursing_specialization
      if (formData.work_experience) payload.work_experience = formData.work_experience
      if (formData.work_settings) payload.work_settings = formData.work_settings
      await UserService.updateUser(user.sub, payload)
      return true
    } catch {
      setSaveError('Could not save your info. You can update it later in your profile.')
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleNext = async () => {
    // Save profile data when leaving Step 2 (index 1)
    if (currentStep === 1) {
      const ok = await saveProfileData()
      if (!ok) return
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1)
    } else {
      handleClose()
    }
  }

  const handleClose = () => {
    clearSignupFlow()
    if (user?.sub) {
      markOnboardingDone(user.sub)
    }
    setVisible(false)
  }

  const handleFindHospital = () => {
    // Capture the current path BEFORE closing/navigating (history.push changes it immediately)
    const currentPath = window.location.pathname
    const wasOnReviewPage = currentPath.startsWith('/review-hospital/') || currentPath.startsWith('/review-agency/')

    handleClose()
    if (selectedResult) {
      const path =
        selectedResult.type === 'hospital'
          ? `/review-hospital/${selectedResult.id}`
          : `/review-agency/${selectedResult.id}`

      if (currentPath === path) {
        // Already on this exact page — force reload
        window.location.reload()
      } else if (wasOnReviewPage) {
        // Already on a different review page (same route pattern, different id) —
        // React Router won't remount the component, so force a full navigation
        window.location.href = path
      } else {
        // Coming from a non-review page — normal SPA navigation works fine
        history.push(path)
      }
    } else {
      // No selection — go to the browse page with the search query
      const path = searchQuery.trim() ? `/hospitals?q=${encodeURIComponent(searchQuery.trim())}` : '/hospitals'
      history.push(path)
    }
  }

  if (!visible) return null

  const isLastStep = currentStep === steps.length - 1

  const stepContent = [
    <Step1 key={1} />,
    <Step2 key={2} formData={formData} onChange={handleFormChange} saving={saving} error={saveError} />,
    <Step3
      key={3}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      autocompleteResults={autocompleteResults}
      isSearching={isSearching}
      onSelectResult={handleSelectResult}
    />,
  ]

  const modal = (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        inset: 0,
        justifyContent: 'center',
        padding: '0 16px',
        position: 'fixed',
        zIndex: 999999,
      }}
    >
      {/* Backdrop */}
      <div
        style={{
          backdropFilter: 'blur(4px)',
          background: 'rgba(0,0,0,0.55)',
          inset: 0,
          position: 'absolute',
        }}
        onClick={handleClose}
      />

      {/* Card */}
      <div
        style={{
          background: 'white',
          borderRadius: '1rem',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          maxWidth: '24rem',
          overflow: 'visible',
          position: 'relative',
          width: '100%',
          zIndex: 1,
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            alignItems: 'center',
            background: 'rgba(255,255,255,0.85)',
            border: 'none',
            borderRadius: '50%',
            color: '#666',
            cursor: 'pointer',
            display: 'flex',
            height: '28px',
            justifyContent: 'center',
            position: 'absolute',
            right: '12px',
            top: '12px',
            width: '28px',
            zIndex: 2,
          }}
          aria-label="Close"
        >
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Step content */}
        {stepContent[currentStep]}

        {/* Footer: dots + buttons */}
        <div className="px-8 pb-6">
          {/* Step 3: Find a hospital layout */}
          {currentStep === 2 ? (
            <>
              <div className="flex items-center justify-between mb-3">
                {/* Pagination dots */}
                <div className="flex gap-2">
                  {steps.map((_, i) => (
                    <div
                      key={i}
                      className={`rounded-full transition-all ${
                        i === currentStep ? 'w-4 h-3 bg-aqua-mint' : 'w-3 h-3 bg-gray-200'
                      }`}
                    />
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentStep((s) => s - 1)}
                    className="text-gray-400 font-semibold px-4 py-2.5 rounded-full text-sm hover:text-gray-600 transition-colors border border-gray-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleFindHospital}
                    className="bg-orange text-white font-semibold px-5 py-2.5 rounded-full hover:bg-orange/90 transition-all shadow-md text-sm"
                  >
                    {selectedResult ? 'Write a review' : 'Find a hospital'}
                  </button>
                </div>
              </div>

              {/* Skip for now */}
              <div className="text-center">
                <button onClick={handleNext} className="text-gray-400 text-sm hover:text-gray-600 transition-colors">
                  Skip for now
                </button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between">
              {/* Pagination dots */}
              <div className="flex gap-2">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentStep(i)}
                    className={`rounded-full transition-all ${
                      i === currentStep ? 'w-4 h-3 bg-aqua-mint' : 'w-3 h-3 bg-gray-200'
                    }`}
                    aria-label={`Go to step ${i + 1}`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-2">
                {/* Back button on Step 2+ */}
                {currentStep > 0 && (
                  <button
                    onClick={() => setCurrentStep((s) => s - 1)}
                    className="text-gray-400 font-semibold px-4 py-2.5 rounded-full text-sm hover:text-gray-600 transition-colors"
                  >
                    Back
                  </button>
                )}

                {/* Next / Get started */}
                <button
                  onClick={handleNext}
                  disabled={saving}
                  className="flex items-center gap-2 bg-orange text-white font-semibold px-6 py-2.5 rounded-full hover:bg-opacity-90 transition-all shadow-md text-sm disabled:opacity-60"
                >
                  {isLastStep ? 'Get started' : 'Continue'}
                  {!isLastStep && (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // Render directly into document.body so Ionic's stacking context cannot clip it
  return ReactDOM.createPortal(modal, document.body)
}
